/**
 * Canvas-env state. Tracks whether the dashboard is currently targeting
 * `prod` or `test` Canvas. Persisted in localStorage so refreshes don't
 * silently flip you back to the default.
 *
 * Default = "test" — safest possible default since prod submissions are
 * irreversible.
 *
 * The context + `useCanvasEnv` hook live in `./envContext` (non-component
 * module) so this file can export only `<EnvProvider>` — required for React
 * Fast Refresh.
 */

import { useEffect, useState, type ReactNode } from "react";
import type { CanvasEnv } from "@canvas-sync/shared";
import { setApiCanvasEnv } from "../api/client";
import { EnvContext } from "./envContext";

const STORAGE_KEY = "canvas-sync.canvasEnv";

function loadInitial(): CanvasEnv {
  if (typeof window === "undefined") return "test";
  return window.localStorage.getItem(STORAGE_KEY) === "prod" ? "prod" : "test";
}

// Prime the API client's env synchronously at import time. EnvProvider also
// mirrors `env` to the client in an effect, but parent effects run AFTER child
// effects — so a page that fires its first fetch from a mount effect could
// otherwise send the stale "test" default even when the operator's persisted
// env is "prod". Setting it here closes that window before any component
// renders (auth happens to mask it today, but this shouldn't depend on that).
setApiCanvasEnv(loadInitial());

export function EnvProvider({ children }: { children: ReactNode }) {
  const [env, setEnvState] = useState<CanvasEnv>(loadInitial);

  // Mirror to (a) localStorage, (b) the shared API client (so every fetch
  // sends the right `?env=`), (c) document root data-env (for CSS theming).
  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, env);
    setApiCanvasEnv(env);
    document.documentElement.setAttribute("data-env", env);
  }, [env]);

  return (
    <EnvContext.Provider value={{ env, setEnv: setEnvState }}>
      {children}
    </EnvContext.Provider>
  );
}
